import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { SlidersHorizontal } from 'lucide-react';
import { FilterPanel, DateRangePill } from '@ooh/ui/components/filter-panel';
import type { FilterField, RangePillLabels } from '@ooh/ui/components/filter-panel';
import type { FilterPanelLabels } from '@ooh/ui/components/filter-pill-primitives';
import { BooleanFilterPill } from '@ooh/ui/components/boolean-filter-pill';
import { Button } from '@ooh/ui/components/button';
import { NearPoiFilterSection } from '../features/pois/components/near-poi-filter-section';
import { isPoiFilterActive, type PoiFilterParams } from '../hooks/use-poi-filter-params';
import {
  NEAR_POI_FILTER_ID,
  NO_PHOTO_FILTER_ID,
  AVAILABILITY_FILTER_ID,
} from '../lib/filter-config';

// eslint-disable-next-line complexity -- three extra pills (near-POI, no-photo, availability), each with its own visibility/render branch, mirror the catalog's identical toolbar wiring (routes/media-units/index.tsx)
export function MapFilterToolbar({
  showFilters,
  setShowFilters,
  filterFields,
  filterValue,
  setFilterValue,
  filterPanelLabels,
  rangeLabels,
  activeFilterCount,
  pinCount,
  poiFilterParams,
  setPoiFilterParams,
  nearPoiFilterLabel,
  nearPoiName,
  noPhoto,
  setNoPhoto,
  availabilityRange,
  setAvailabilityRange,
  noPhotoFilterLabel,
  availabilityFilterLabel,
  onClearSearch,
  t,
  i18n,
}: {
  showFilters: boolean;
  setShowFilters: (v: boolean) => void;
  filterFields: FilterField[];
  filterValue: Record<string, string[]>;
  setFilterValue: (v: Record<string, string[]>) => void;
  filterPanelLabels: FilterPanelLabels;
  /** RangePill's own Min/Max/Apply/Clear labels — required once any `type: 'range'` field renders. */
  rangeLabels: RangePillLabels;
  activeFilterCount: number;
  pinCount: number;
  poiFilterParams: PoiFilterParams;
  setPoiFilterParams: (v: PoiFilterParams) => void;
  /** Translated label for the Near POI entry in the "Dodaj filtr" menu */
  nearPoiFilterLabel: string;
  /** Resolved POI name for per-POI chip label (optional) */
  nearPoiName?: string;
  /** "bez zdjęcia" boolean sidecar (map-catalog-filter-parity), mirrors /media-units. */
  noPhoto: boolean;
  setNoPhoto: (v: boolean) => void;
  /** Availability date-range sidecar `[from, to]`, mirrors /media-units. */
  availabilityRange: string[];
  setAvailabilityRange: (v: string[]) => void;
  /** Translated label for the "bez zdjęcia" entry in the "Dodaj filtr" menu */
  noPhotoFilterLabel: string;
  /** Translated label for the availability entry in the "Dodaj filtr" menu */
  availabilityFilterLabel: string;
  onClearSearch?: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
  i18n: ReturnType<typeof useTranslation>['i18n'];
}) {
  // Bumped by "Wyczyść filtry" to force-remount FilterPanel below — it seeds its
  // internal visibleFilterIds/visibleExtraIds (pill visibility) only once via a lazy
  // useState initializer, with no external reset hook, so a data-only clear leaves
  // pills visually stuck. See map-filter-toolbar.test.tsx.
  const [resetNonce, setResetNonce] = React.useState(0);
  // "Wyczyść filtry" clears filterValue/poiFilterParams/noPhoto/availabilityRange via
  // navigate()-backed setters (async — each round-trips through the router before this
  // component re-renders with the new props). Bumping resetNonce in the SAME click
  // handler remounts FilterPanel against the STILL-STALE props from that render, so it
  // re-seeds visibleExtraIds as if nothing was cleared — the remount fires one render
  // too early. Deferring the bump to an effect that fires only once every prop this
  // toolbar owns has actually settled to its cleared value closes that race regardless
  // of how many intermediate renders the navigation takes.
  const clearPendingRef = React.useRef(false);
  // `filterValue` (actually `panelFilterValue` from useLocationFilterPanel) always
  // carries a `location` key once filter options have loaded — `{ ...filterValue,
  // location: buildLocationPanelValue(filterValue) }` — so it never becomes a
  // zero-key object even when every dimension is cleared. Checking key COUNT
  // instead of value emptiness meant this condition never became true after the
  // very first load, so the remount never fired at all (verified against a live
  // browser repro, not just the unit harness below, which used a plain `{}` and
  // therefore missed this).
  const allFiltersCleared =
    Object.values(filterValue).every((v) => v.length === 0) &&
    !noPhoto &&
    availabilityRange.length === 0 &&
    !isPoiFilterActive(poiFilterParams);
  React.useEffect(() => {
    if (clearPendingRef.current && allFiltersCleared) {
      clearPendingRef.current = false;
      setResetNonce((n) => n + 1);
    }
  }, [allFiltersCleared]);

  const isPerPoiFilterActive = Boolean(poiFilterParams.nearPoiId && poiFilterParams.nearRadius);
  const totalActiveCount =
    activeFilterCount +
    (isPoiFilterActive(poiFilterParams) ? 1 : 0) +
    (noPhoto ? 1 : 0) +
    (availabilityRange.length > 0 ? 1 : 0);

  const extraHiddenItems = [
    { id: NEAR_POI_FILTER_ID, label: nearPoiFilterLabel },
    { id: NO_PHOTO_FILTER_ID, label: noPhotoFilterLabel },
    { id: AVAILABILITY_FILTER_ID, label: availabilityFilterLabel },
  ];

  // Seeds pills as visible if their params are already in the URL (e.g. shared link /
  // back nav) — mirrors the catalog's identical defaultVisibleExtraIds wiring
  // (routes/media-units/index.tsx). FilterPanel reads this only via a lazy useState
  // initializer, so recomputing it every render (no memo) is safe.
  const defaultVisibleExtraIds: string[] = [];
  if (isPoiFilterActive(poiFilterParams)) defaultVisibleExtraIds.push(NEAR_POI_FILTER_ID);
  if (noPhoto) defaultVisibleExtraIds.push(NO_PHOTO_FILTER_ID);
  if (availabilityRange.length > 0) defaultVisibleExtraIds.push(AVAILABILITY_FILTER_ID);

  // Boolean filters apply on add (Polaris appliedFilters model): selecting "bez
  // zdjęcia" from the menu immediately sets the value — mirrors the catalog's
  // identical handleShowExtra (routes/media-units/index.tsx).
  const handleShowExtra = (id: string) => {
    if (id === NO_PHOTO_FILTER_ID) setNoPhoto(true);
  };

  const renderExtraPill = (id: string, onHide: () => void) => {
    if (id === NEAR_POI_FILTER_ID) {
      return (
        <NearPoiFilterSection
          value={poiFilterParams}
          onChange={(params) => {
            setPoiFilterParams(params);
          }}
          onHide={() => {
            setPoiFilterParams({});
            onHide();
          }}
          nearPoiName={nearPoiName}
        />
      );
    }
    if (id === NO_PHOTO_FILTER_ID) {
      return (
        <BooleanFilterPill
          label={noPhotoFilterLabel}
          onClear={() => {
            setNoPhoto(false);
            onHide();
          }}
          clearAriaLabel={t('filters.clearLabel')}
          testId="filter-no-photo"
        />
      );
    }
    if (id === AVAILABILITY_FILTER_ID) {
      return (
        <DateRangePill
          field={{ type: 'dateRange', id: AVAILABILITY_FILTER_ID, label: availabilityFilterLabel }}
          selected={availabilityRange}
          onChange={(_id, values) => setAvailabilityRange(values)}
          onClear={() => {
            setAvailabilityRange([]);
            onHide();
          }}
          labels={filterPanelLabels}
        />
      );
    }
    return null;
  };

  if (showFilters) {
    return (
      <>
        <div className="shrink-0 px-4 pt-4">
          <FilterPanel
            key={resetNonce}
            fields={filterFields}
            value={filterValue}
            onChange={setFilterValue}
            labels={filterPanelLabels}
            rangeLabels={rangeLabels}
            extraHiddenItems={extraHiddenItems}
            renderExtraPill={renderExtraPill}
            defaultVisibleExtraIds={defaultVisibleExtraIds}
            onShowExtra={handleShowExtra}
            onClearAll={onClearSearch}
          />
          <div className="flex items-center justify-between pt-2 mt-2 border-t border-border/40 pb-2">
            <span className="text-[12px] text-muted-foreground">
              {t('filters.foundCount')}{' '}
              <span className="font-semibold text-foreground">
                {pinCount.toLocaleString(i18n.language)}
              </span>{' '}
              {t('filters.pinsLabel')}
            </span>
            {totalActiveCount > 0 && (
              <Button
                variant="inline"
                type="button"
                onClick={() => {
                  clearPendingRef.current = true;
                  setFilterValue({});
                  setPoiFilterParams({});
                  setNoPhoto(false);
                  setAvailabilityRange([]);
                  onClearSearch?.();
                }}
                data-testid="clear-filters"
              >
                {t('filters.clearFiltersLabel')}
              </Button>
            )}
          </div>
        </div>
        <div className="shrink-0 px-4 pb-2">
          <Button
            variant="inline"
            type="button"
            onClick={() => setShowFilters(false)}
            data-testid="filter-toggle-collapse"
          >
            {String(t('filters.hideLabel'))}
          </Button>
        </div>
      </>
    );
  }

  return (
    <div className="shrink-0 px-4 py-2 flex items-center gap-3 border-b border-border/40 flex-wrap">
      <Button
        variant="secondary"
        size="xs"
        onClick={() => setShowFilters(true)}
        data-testid="filter-toggle"
      >
        <SlidersHorizontal className="h-4 w-4" />
        {t('filters.toggleLabel')}
        {totalActiveCount > 0 && (
          <span className="ml-1 bg-primary/10 text-primary text-[10px] rounded-full px-1.5 py-0.5 font-semibold">
            {totalActiveCount}
          </span>
        )}
      </Button>
      {isPerPoiFilterActive ? (
        <NearPoiFilterSection
          value={poiFilterParams}
          onChange={setPoiFilterParams}
          onHide={() => setPoiFilterParams({})}
          nearPoiName={nearPoiName}
        />
      ) : null}
      <span className="text-[12px] text-muted-foreground">
        <span className="font-semibold text-foreground">
          {pinCount.toLocaleString(i18n.language)}
        </span>{' '}
        {t('filters.countLabel')}
      </span>
    </div>
  );
}
